import { animate, style, transition, trigger } from '@angular/animations';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostListener,
  inject,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges
} from '@angular/core';
import { Router } from '@angular/router';

import { select, Store } from '@ngrx/store';

import { findIndex, isNil, isNull, isUndefined } from 'lodash-es';
import { of, Subscription } from 'rxjs';
import { concatMap, filter } from 'rxjs/operators';

import * as fromWalkingFarmSelectors from '../../../store/selectors/walking-farm.selectors';

@Component({
  animations: [
    trigger('showInOutAnimation', [
      transition(':enter', [style({ height: 0 }), animate('110ms ease-out', style({ height: 44 }))]),
      transition(':leave', [style({ height: 44 }), animate('110ms ease-in', style({ height: 0 }))])
    ])
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'red-property-navigator',
  standalone: false,
  styleUrls: ['./property-navigator.component.scss'],
  templateUrl: './property-navigator.component.html'
})
export class PropertyNavigatorComponent implements OnChanges, OnInit {
  private changeDetectorRef = inject(ChangeDetectorRef);
  private store = inject(Store);
  private router = inject(Router);

  @Input() walkingFarmId: string | null = null;
  @Input() propertyId: string | null = null;
  previousPropertyId: string | null = null;
  nextPropertyId: string | null = null;

  currentPropertyDisplayIndex: number | null = null;
  totalProperties: number | null = null;

  walkingFarm$?: Subscription;

  ngOnInit() {
    this.handleNavigationChange();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['farmId']) {
      this.handleNavigationChange();
    }
  }

  handleNavigationChange() {
    this.walkingFarm$?.unsubscribe();
    this.walkingFarm$ = of(this.walkingFarmId)
      .pipe(
        filter((id) => !isNil(id)),
        concatMap((id) => this.store.pipe(select(fromWalkingFarmSelectors.selectWalkingFarm(id)))),
        filter((walkingFarm) => !isUndefined(walkingFarm))
      )
      .subscribe((walkingFarm) => {
        this.currentPropertyDisplayIndex =
          findIndex(walkingFarm.properties, (item) => {
            return `${item.A000_PropertyNumber}` === this.propertyId;
          }) + 1;
        if (this.currentPropertyDisplayIndex === 0) {
          this.currentPropertyDisplayIndex = null;
        }
        this.totalProperties = walkingFarm.properties.length;

        this.nextPropertyId = null;
        this.previousPropertyId = null;

        if (!isNull(this.currentPropertyDisplayIndex)) {
          if (this.currentPropertyDisplayIndex < this.totalProperties) {
            this.nextPropertyId = `${walkingFarm.properties[this.currentPropertyDisplayIndex].A000_PropertyNumber}`;
          }
          if (this.currentPropertyDisplayIndex > 1) {
            this.previousPropertyId = `${walkingFarm.properties[this.currentPropertyDisplayIndex - 2].A000_PropertyNumber}`;
          }
        }
        this.changeDetectorRef.detectChanges();
      });
  }

  @HostListener('window:keydown', ['$event'])
  async handleKeyboardEvent(event: KeyboardEvent) {
    const key = event.key;

    if (key === 'ArrowLeft') {
      await this.previous();
    } else if (key === 'ArrowRight') {
      await this.next();
    }
  }

  async previous() {
    if (this.previousPropertyId) {
      this.propertyId = this.previousPropertyId;
      this.handleNavigationChange();
      await this.router.navigate(['/walking-farm', this.walkingFarmId, 'view', 'property', this.propertyId], {
        replaceUrl: true
      });
    }
  }

  async next() {
    if (this.nextPropertyId) {
      this.propertyId = this.nextPropertyId;
      this.handleNavigationChange();
      await this.router.navigate(['/walking-farm', this.walkingFarmId, 'view', 'property', this.propertyId], {
        replaceUrl: true
      });
    }
  }
}
